import { useEffect, useMemo, useState } from 'react';
import {
  copyCourseContent
} from '../../../utils/courseCopy';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;
const API_URL = process.env.REACT_APP_API_URL;

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function headers(extra = {}) {
  return {
    apikey: SUPABASE_KEY,
    Authorization: 'Bearer ' + SUPABASE_KEY,
    ...extra
  };
}

async function json(response, label) {
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`${label} failed (${response.status}): ${detail}`);
  }
  if (response.status === 204) return null;
  return response.json();
}

function courseDays(course) {
  const days =
    Array.isArray(course?.class_days) && course.class_days.length
      ? course.class_days
      : course?.class_day
        ? [course.class_day]
        : [];

  return Array.from(new Set(days.filter(day => DAYS.includes(day))))
    .sort((a, b) => DAYS.indexOf(a) - DAYS.indexOf(b));
}

function remapDayValues(values, oldDays, newDays) {
  const source = values && typeof values === 'object' ? values : {};
  const next = {};

  oldDays
    .filter(day => newDays.includes(day))
    .forEach(day => {
      if (Object.prototype.hasOwnProperty.call(source, day)) {
        next[day] = source[day];
      }
    });

  const removed = oldDays.filter(day => !newDays.includes(day));
  const added = newDays.filter(day => !oldDays.includes(day));

  for (let i = 0; i < Math.min(removed.length, added.length); i += 1) {
    if (Object.prototype.hasOwnProperty.call(source, removed[i])) {
      next[added[i]] = source[removed[i]];
    }
  }

  return next;
}

function generateWeeks(courseId, startDate, classDays, weekCount) {
  const dayIndex = {
    Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3,
    Thursday: 4, Friday: 5, Saturday: 6
  };

  const count = Number(weekCount);
  if (!Number.isInteger(count) || count < 1) {
    throw new Error('The target semester does not have a valid week count.');
  }

  const start = new Date(`${startDate}T12:00:00Z`);
  if (Number.isNaN(start.getTime())) {
    throw new Error('The target semester does not have a valid start date.');
  }

  const weeks = [];

  classDays.forEach(day => {
    const first = new Date(start);
    while (first.getUTCDay() !== dayIndex[day]) {
      first.setUTCDate(first.getUTCDate() + 1);
    }

    for (let i = 0; i < count; i += 1) {
      const date = new Date(first);
      date.setUTCDate(first.getUTCDate() + i * 7);

      weeks.push({
        course_id: courseId,
        week_number: i + 1,
        class_date: date.toISOString().slice(0, 10),
        cq_enabled: i < 16,
        attendance_enabled: i < 16,
        pd_enabled: false
      });
    }
  });

  weeks.sort((a, b) => a.class_date.localeCompare(b.class_date));

  const pdWeeks = new Set();
  weeks.forEach(week => {
    if (week.week_number <= 16 && !pdWeeks.has(week.week_number)) {
      week.pd_enabled = true;
      pdWeeks.add(week.week_number);
    }
  });

  return weeks;
}

async function fetchSemesters(institutionId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/semesters?institution_id=eq.${encodeURIComponent(institutionId)}&select=id,name,start_date,week_count,institution_id&order=start_date.asc`,
    { headers: headers() }
  );
  return json(response, 'Loading semesters');
}

async function fetchSourceCourse(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/courses?id=eq.${courseId}&select=*&limit=1`,
    { headers: headers() }
  );
  const rows = await json(response, 'Loading source course');
  return rows?.[0] || null;
}

async function fetchActiveEnrolments(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/enrolments?course_id=eq.${courseId}&active=eq.true&select=student_id,stream,group_number,assigned_instructor_id`,
    { headers: headers() }
  );
  return json(response, 'Loading active students');
}

async function fetchCourseAccess(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_access?course_id=eq.${courseId}&select=instructor_id`,
    { headers: headers() }
  );
  return json(response, 'Loading secondary instructors');
}


async function fetchPlacementLabels(courseId) {
  const [streamResponse, groupResponse] = await Promise.all([
    fetch(
      `${SUPABASE_URL}/rest/v1/course_stream_labels?course_id=eq.${courseId}&select=stream_number,name`,
      { headers: headers() }
    ),
    fetch(
      `${SUPABASE_URL}/rest/v1/course_group_labels?course_id=eq.${courseId}&select=stream_number,group_number,name,description`,
      { headers: headers() }
    )
  ]);

  const [streams, groups] = await Promise.all([
    json(streamResponse, 'Loading Stream labels'),
    json(groupResponse, 'Loading Group labels')
  ]);

  return {
    streams: Array.isArray(streams) ? streams : [],
    groups: Array.isArray(groups) ? groups : []
  };
}

async function copyPlacementLabels(targetCourseId, labels) {
  const requests = [];

  if (labels.streams.length) {
    requests.push(
      fetch(
        `${SUPABASE_URL}/rest/v1/course_stream_labels`,
        {
          method: 'POST',
          headers: headers({
            'Content-Type': 'application/json',
            Prefer: 'resolution=merge-duplicates'
          }),
          body: JSON.stringify(labels.streams.map(row => ({
            course_id: targetCourseId,
            stream_number: row.stream_number,
            name: row.name
          })))
        }
      ).then(response => json(response, 'Carrying Stream labels forward'))
    );
  }

  if (labels.groups.length) {
    requests.push(
      fetch(
        `${SUPABASE_URL}/rest/v1/course_group_labels`,
        {
          method: 'POST',
          headers: headers({
            'Content-Type': 'application/json',
            Prefer: 'resolution=merge-duplicates'
          }),
          body: JSON.stringify(labels.groups.map(row => ({
            course_id: targetCourseId,
            stream_number: row.stream_number,
            group_number: row.group_number,
            name: row.name,
            description: row.description || null
          })))
        }
      ).then(response => json(response, 'Carrying Group labels forward'))
    );
  }

  await Promise.all(requests);
}

async function createCourse(payload) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/courses`,
    {
      method: 'POST',
      headers: headers({
        'Content-Type': 'application/json',
        Prefer: 'return=representation'
      }),
      body: JSON.stringify(payload)
    }
  );
  const rows = await json(response, 'Creating extended course');
  const created = rows?.[0] || null;
  if (!created?.id) throw new Error('Course creation returned no course record.');
  return created;
}

async function createWeeks(rows) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks`,
    {
      method: 'POST',
      headers: headers({
        'Content-Type': 'application/json',
        Prefer: 'return=representation'
      }),
      body: JSON.stringify(rows)
    }
  );
  return json(response, 'Creating target semester weeks');
}

async function addSelfDirectedStudyLabels(weeks) {
  const target = (Array.isArray(weeks) ? weeks : [])
    .filter(week => Number(week.week_number) >= 17);

  if (!target.length) return;

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_week_content`,
    {
      method: 'POST',
      headers: headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(target.map(week => ({
        course_week_id: week.id,
        description: '<p>Self-Directed Study Week</p>'
      })))
    }
  );
  await json(response, 'Adding self-directed study labels');
}

async function copyEnrolments(targetCourseId, rows) {
  if (!rows.length) return;

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/enrolments`,
    {
      method: 'POST',
      headers: headers({
        'Content-Type': 'application/json',
        Prefer: 'resolution=merge-duplicates'
      }),
      body: JSON.stringify(rows.map(row => ({
        student_id: row.student_id,
        course_id: targetCourseId,
        active: true,
        stream: row.stream ?? null,
        group_number: row.group_number ?? null,
        assigned_instructor_id: row.assigned_instructor_id ?? null
      })))
    }
  );
  await json(response, 'Carrying active students forward');
}

async function copyCourseAccess(targetCourseId, rows, primaryInstructorId) {
  const ids = Array.from(new Set(
    (Array.isArray(rows) ? rows : [])
      .map(row => row.instructor_id)
      .filter(id => id && id !== primaryInstructorId)
  ));

  if (!ids.length) return;

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_access`,
    {
      method: 'POST',
      headers: headers({
        'Content-Type': 'application/json',
        Prefer: 'resolution=merge-duplicates'
      }),
      body: JSON.stringify(ids.map(instructorId => ({
        course_id: targetCourseId,
        instructor_id: instructorId
      })))
    }
  );
  await json(response, 'Carrying secondary instructors forward');
}

async function createReadingsFolder(courseName, semesterName, courseId) {
  if (!API_URL) return;

  await fetch(API_URL, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'createCourseReadingsFolder',
      courseName: `${courseName} - ${semesterName}`,
      courseId
    })
  });
}

async function cleanupCourse(courseId) {
  if (!courseId) return;
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/courses?id=eq.${courseId}`,
    { method: 'DELETE', headers: headers() }
  );
  if (!response.ok) {
    console.error('Failed to clean up incomplete extended course:', await response.text());
  }
}

function copyIfPresent(target, source, field) {
  if (Object.prototype.hasOwnProperty.call(source, field)) {
    target[field] = source[field];
  }
}

function buildTargetCourse(source, targetSemesterId, targetDays, progressMode) {
  const oldDays = courseDays(source);
  const classTimes = remapDayValues(source.class_times || {}, oldDays, targetDays);
  const classrooms = remapDayValues(source.classrooms || {}, oldDays, targetDays);

  const payload = {
    semester_id: targetSemesterId,
    course_title_id: source.course_title_id || null,
    name: source.name || null,
    instructor_id: source.instructor_id,
    course_series_id: source.course_series_id,
    series_progress_mode: progressMode,
    class_days: targetDays,
    class_day: targetDays.length === 1 ? targetDays[0] : null,
    class_times: classTimes,
    class_time: targetDays.length === 1 ? (classTimes[targetDays[0]] || null) : null,
    classrooms,
    is_live: false,
    mid_eval_open_date: null,
    mid_eval_close_date: null,
    final_eval_open_date: null,
    final_eval_close_date: null
  };

  [
    'pd_due_day',
    'cq_clarificatory_enabled',
    'cq_clarificatory_allowed',
    'cq_due_hours_before_class',
    'qi_enabled',
    'qi_groups_per_stream',
    'streams_enabled',
    'group_based_attendance',
    'automated_checkin_enabled',
    'variable_timings_enabled',
    'variable_rooms_enabled',
    'attendance_grace_absences',
    'attendance_min_contribution',
    'attendance_penalty_pattern',
    'attendance_penalty_increase_percent',
    'course_image_drive_file_id',
    'is_honors',
    'grading_mode',
    'pd_enabled',
    'cq_enabled',
    'attendance_enabled',
    'self_eval_enabled'
  ].forEach(field => copyIfPresent(payload, source, field));

  return payload;
}

export default function CourseExtendModal({
  course,
  session,
  onClose,
  onExtended
}) {
  const [source, setSource] = useState(null);
  const [semesters, setSemesters] = useState([]);
  const [targetSemesterId, setTargetSemesterId] = useState('');
  const [classDays, setClassDays] = useState(courseDays(course));
  const [progressMode, setProgressMode] = useState('restart');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      fetchSourceCourse(course.id),
      fetchSemesters(session.institutionId)
    ])
      .then(([loadedSource, loadedSemesters]) => {
        if (cancelled) return;
        if (!loadedSource) throw new Error('The source course could not be found.');
        if (!loadedSource.course_series_id) {
          throw new Error('Run the course-series SQL migration before extending courses.');
        }
        setSource(loadedSource);

        if (loadedSource.grading_mode === 'traditional') {
          setProgressMode('restart');
        }

        setClassDays(courseDays(loadedSource));
        setSemesters(Array.isArray(loadedSemesters) ? loadedSemesters : []);
      })
      .catch(err => {
        if (!cancelled) setError(err?.message || 'Could not load extension settings.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [course.id, session.institutionId]);

  const sourceSemester = useMemo(
    () => semesters.find(semester => semester.id === course.semester_id) || null,
    [semesters, course.semester_id]
  );

  const targetSemesters = useMemo(
    () => semesters.filter(semester =>
      semester.id !== course.semester_id &&
      (
        !sourceSemester?.start_date ||
        !semester.start_date ||
        semester.start_date > sourceSemester.start_date
      )
    ),
    [semesters, course.semester_id, sourceSemester]
  );

  function toggleDay(day, checked) {
    setClassDays(previous => {
      const next = checked
        ? [...previous, day]
        : previous.filter(value => value !== day);

      return Array.from(new Set(next))
        .sort((a, b) => DAYS.indexOf(a) - DAYS.indexOf(b));
    });
  }

  async function extendCourse() {
    if (saving || !source) return;

    if (!targetSemesterId) {
      setError('Select the semester to extend into.');
      return;
    }

    if (!classDays.length) {
      setError('Select at least one class day.');
      return;
    }

    const targetSemester = semesters.find(
      semester => semester.id === targetSemesterId
    );

    if (!targetSemester) {
      setError('The selected semester is no longer available.');
      return;
    }

    setSaving(true);
    setError('');
    let createdCourse = null;

    try {
      const existingResponse = await fetch(
        `${SUPABASE_URL}/rest/v1/courses?course_series_id=eq.${encodeURIComponent(source.course_series_id)}&semester_id=eq.${encodeURIComponent(targetSemesterId)}&select=id&limit=1`,
        { headers: headers() }
      );

      const existing = await json(existingResponse, 'Checking existing series offerings');

      if (Array.isArray(existing) && existing.length) {
        throw new Error('This course series already has an offering in that semester.');
      }

      const [activeEnrolments, accessRows, placementLabels] = await Promise.all([
        fetchActiveEnrolments(course.id),
        fetchCourseAccess(course.id),
        fetchPlacementLabels(course.id)
      ]);

      createdCourse = await createCourse(
        buildTargetCourse(
          source,
          targetSemesterId,
          classDays,
          progressMode
        )
      );

      const weeks = await createWeeks(
        generateWeeks(
          createdCourse.id,
          targetSemester.start_date,
          classDays,
          targetSemester.week_count
        )
      );

      await addSelfDirectedStudyLabels(
        weeks
      );

      await copyCourseContent({
        sourceCourseId:
          course.id,
        targetCourseId:
          createdCourse.id
      });

      await Promise.all([
        copyEnrolments(
          createdCourse.id,
          Array.isArray(activeEnrolments) ? activeEnrolments : []
        ),
        copyCourseAccess(
          createdCourse.id,
          Array.isArray(accessRows) ? accessRows : [],
          source.instructor_id
        ),
        copyPlacementLabels(
          createdCourse.id,
          placementLabels
        )
      ]);

      await createReadingsFolder(
        course.course_titles?.name || course.name || 'Course',
        targetSemester.name || '',
        createdCourse.id
      );

      await onExtended(createdCourse);
    } catch (err) {
      if (createdCourse?.id) {
        await cleanupCourse(createdCourse.id);
      }
      setError(err?.message || 'The course could not be extended.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      style={styles.overlay}
      onClick={saving ? undefined : onClose}
    >
      <div
        style={styles.modal}
        onClick={event => event.stopPropagation()}
      >
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>Extend Course</h2>
            <div style={styles.subtitle}>
              {course.course_titles?.name || course.name || 'Course'}
            </div>
          </div>

          <button
            type="button"
            style={styles.close}
            disabled={saving}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {loading ? (
          <div style={styles.muted}>Loading…</div>
        ) : (
          <>
            <div style={styles.field}>
              <label style={styles.label}>Extend into semester</label>
              <select
                style={styles.input}
                value={targetSemesterId}
                disabled={saving}
                onChange={event => setTargetSemesterId(event.target.value)}
              >
                <option value="">Select a semester</option>
                {targetSemesters.map(semester => (
                  <option key={semester.id} value={semester.id}>
                    {semester.name}
                  </option>
                ))}
              </select>
              {!targetSemesters.length && (
                <div style={styles.help}>
                  No later semester is configured for this institution.
                </div>
              )}
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Class day(s)</label>
              <div style={styles.days}>
                {DAYS.map(day => (
                  <label key={day} style={styles.dayLabel}>
                    <input
                      type="checkbox"
                      checked={classDays.includes(day)}
                      disabled={saving}
                      onChange={event => toggleDay(day, event.target.checked)}
                    />
                    {day}
                  </label>
                ))}
              </div>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Progress across semesters</label>

              <label style={styles.radio}>
                <input
                  type="radio"
                  name="seriesProgressMode"
                  checked={progressMode === 'restart'}
                  disabled={saving}
                  onChange={() => setProgressMode('restart')}
                />
                <span>
                  <strong>Restart</strong> — the new semester has its own progress counts.
                </span>
              </label>

              <label style={styles.radio}>
                <input
                  type="radio"
                  name="seriesProgressMode"
                  checked={progressMode === 'continue'}
                  disabled={
                    saving ||
                    source?.grading_mode === 'traditional'
                  }
                  onChange={() => setProgressMode('continue')}
                />
                <span>
                  <strong>Continue</strong> — carry eligible ungrading progress counts forward from the preceding semester.
                  {source?.grading_mode === 'traditional'
                    ? ' Traditional grading always restarts because weighted grades are semester-specific.'
                    : ''}
                </span>
              </label>
            </div>

            <div style={styles.note}>
              The new offering starts non-live. Active students and their current
              Stream / Group placement are carried forward, but submissions, grades,
              attendance and other student work are not.
            </div>
          </>
        )}

        <div style={styles.footer}>
          <button
            type="button"
            style={styles.cancelButton}
            disabled={saving}
            onClick={onClose}
          >
            Cancel
          </button>

          <button
            type="button"
            style={styles.extendButton}
            disabled={loading || saving || !targetSemesterId || !classDays.length}
            onClick={extendCourse}
          >
            {saving ? 'Extending…' : 'Extend Course'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 500,
    background: 'rgba(0,0,0,0.48)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  },
  modal: {
    width: '100%',
    maxWidth: '620px',
    maxHeight: '90vh',
    overflowY: 'auto',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.22)',
    padding: '22px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '18px'
  },
  title: {
    margin: 0,
    fontSize: '21px',
    color: '#1f2933'
  },
  subtitle: {
    marginTop: '4px',
    color: '#66788a',
    fontSize: '13px'
  },
  close: {
    border: 'none',
    background: 'transparent',
    fontSize: '26px',
    lineHeight: 1,
    cursor: 'pointer',
    color: '#66788a'
  },
  field: {
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    fontWeight: '700',
    fontSize: '13px',
    color: '#1f2933',
    marginBottom: '7px'
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '9px 11px',
    background: 'white',
    fontSize: '14px'
  },
  days: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '9px 15px'
  },
  dayLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '14px'
  },
  radio: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    marginBottom: '9px',
    fontSize: '14px',
    lineHeight: 1.4,
    color: '#334e68'
  },
  note: {
    padding: '11px 13px',
    borderRadius: '8px',
    background: '#f7f9fa',
    border: '1px solid #d7dee5',
    color: '#52616b',
    fontSize: '13px',
    lineHeight: 1.45
  },
  help: {
    marginTop: '6px',
    color: '#66788a',
    fontSize: '12px'
  },
  muted: {
    color: '#66788a',
    fontSize: '14px'
  },
  error: {
    marginBottom: '16px',
    padding: '10px 12px',
    border: '1px solid #e8bcbc',
    borderRadius: '8px',
    background: '#faeaea',
    color: '#a12a2a',
    fontSize: '13px'
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '22px'
  },
  cancelButton: {
    border: '1px solid #b8c6d1',
    borderRadius: '8px',
    padding: '9px 14px',
    background: 'white',
    color: '#334e68',
    fontWeight: '700',
    cursor: 'pointer'
  },
  extendButton: {
    border: 'none',
    borderRadius: '8px',
    padding: '9px 14px',
    background: '#215b7d',
    color: 'white',
    fontWeight: '700',
    cursor: 'pointer'
  }
};
